import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import ImageModal from "../../components/ImageModa";
import MainLayout from "../../components/MainLayout";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import blogService from "../../services/blog";
import contentService from "../../services/content";
import { generatePublicUrl } from "../../utils/generatePublicUrl";
import queryDefault from "../../utils/queryDefault";
import LoadingPage from "../LoadingPage";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function BlogDetail() {
  const { id } = useParams();

  const [imageUrl, setImageUrl] = useState("");
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery("current-blog", () =>
    blogService.getBlogById(Number(id))
  );

  const contentList = useQuery("current-content", () =>
    contentService.getAllContent({ blog_id: id })
  );

  const alert = useAlert();
  const notification = useNotification();

  const [pageSize, setPageSize] = useState<number>(10);
  const [options, setOptions] = useState<any>({});

  const deleteContent = useMutation(
    "delete-content",
    contentService.deleteContent,
    {
      onSuccess: () => {
        contentList.refetch();
        notification.onOpen({
          message: "Content was successfully deleted!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        contentList.refetch();
        notification.onOpen({
          message:
            error?.response?.data?.message === undefined
              ? "Something went wrong"
              : error?.response?.data?.message,
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const handleImageClick = (url: string) => {
    setOpen(true);
    setImageUrl(url);
  };

  const onDelete = (id: number) => {
    alert.onClose();
    deleteContent.mutate(id);
  };

  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete this content?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  return isLoading || contentList.isLoading ? (
    <LoadingPage />
  ) : (
    <MainLayout>
      <Breadcrumb
        label={get(data, "data.title") || ""}
        breadcrumbs={[
          { label: "Blog", href: `/app/cms/blog` },
          { label: "Blog Detail", href: `/app/cms/blog/detail/${id}` },
        ]}
      />

      <Grid container>
        <Grid item lg={6} xs={12}>
          <ImageModal
            open={open}
            src={imageUrl}
            onClose={() => setOpen(false)}
          />
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Title
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data.data.title}
            </Typography>
          </Stack>

          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Banner
            </Typography>
            <img
              src={
                generatePublicUrl(data.data?.banner) ??
                "https://fakeimg.pl/600x400"
              }
              alt="Banner"
              width={500}
            />
          </Stack>
        </Grid>
      </Grid>

      <Typography variant="h2" my={2}>
        Content List
      </Typography>

      <Box sx={{ my: 2 }}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            component={RouterLink}
            to={`/app/cms/blog/${id}/content/create`}
          >
            ADD CONTENT
          </Button>
        </Stack>
      </Box>

      <DataGrid
        autoHeight
        loading={contentList.isLoading}
        getRowId={(record) => get(record, "id")}
        rows={get(contentList, "data.data", [])}
        rowCount={get(contentList, "data.data", []).length}
        page={options.page_number}
        pageSize={pageSize}
        rowsPerPageOptions={[10, 20, 30]}
        pagination
        disableSelectionOnClick
        onPageChange={(newPage) => {
          setOptions((prev: any) => ({ ...prev, page_number: newPage }));
        }}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        components={{
          NoRowsOverlay: CustomNoRowOverlay,
        }}
        columns={[
          {
            field: "desc",
            headerName: "Type",
            flex: 1,
            filterable: false,
            renderCell: (params) => {
              return (
                <Stack>
                  <Typography>{params.value}</Typography>
                </Stack>
              );
            },
          },
          {
            field: "content",
            headerName: "Content",
            flex: 1,
            filterable: false,
            renderCell: (params) => {
              return (
                <Stack>
                  {params.row.type === "img" ? (
                    <Box
                      component="img"
                      src={generatePublicUrl(params.row.img_path) + `?v=${new Date().getTime()}`}
                      alt="Banner"
                      sx={{
                        height: "70px",
                        "&:hover": {
                          cursor: "pointer",
                        },
                      }}
                      onClick={() =>
                        handleImageClick(generatePublicUrl(params.row.img_path) + `?v=${new Date().getTime()}`)
                      }
                    />
                  ) : (
                    <Typography>{params.value}</Typography>
                  )}
                </Stack>
              );
            },
          },
          {
            field: "align",
            headerName: "Align",
            flex: 1,
            filterable: false,
            renderCell: (params) => {
              return (
                <Stack>
                  <Typography>{params.value}</Typography>
                </Stack>
              );
            },
          },
          {
            field: "order",
            headerName: "Order",
            flex: 1,
            filterable: false,
            renderCell: (params) => {
              return (
                <Stack>
                  <Typography>{params.value}</Typography>
                </Stack>
              );
            },
          },
          {
            field: "actions",
            headerName: "Action",
            sortable: false,
            filterable: false,
            width: 200,
            renderCell: (params) => {
              return (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    color="primary"
                    component={RouterLink}
                    to={`/app/cms/blog/content/edit/${get(params, "row.id")}`}
                  >
                    Edit
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => onClickDelete(params.row.id)}
                  >
                    Delete
                  </Button>
                </Stack>
              );
            },
          },
        ]}
        sx={{
          ".MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
          },
        }}
      />
    </MainLayout>
  );
}
